import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Grid } from '@coursera/cds-core';

import TakeACourseBtn from 'bundles/degree-description/components/modules/hero/TakeACourseBtn';
import { useDegreeDescriptionEoiStateContext } from 'bundles/degree-description/contexts/DegreeDescriptionEoiStateContext';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionUpsellsData } from 'bundles/degree-description/contexts/DegreeDescriptionPageUpsellsContext';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import { getManualCopyTest } from 'bundles/degree-description/utils/manualCopyTests';
import { shouldShowEoiStateVariant } from 'bundles/degree-description/utils/utils';
import EoiButton from 'bundles/expression-of-interest/components/EoiButton';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';

type Props = {
  keySuffix: string;
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  trackingNamePrefix?: string;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
  enableGrid?: boolean;
};

const EoiButtonsShared: React.FC<Props> = ({
  keySuffix,
  eoiButtons,
  eoiContainerText,
  trackingNamePrefix,
  trackingIndex,
  setEoiTrackingIndex,
  enableGrid,
}) => {
  const { slug } = useParams();
  const { eoiSurveyData, id } = useDegreeDescriptionPageData();
  const {
    eoiStates: [eoiStateVarState],
    variantOverrides: [showVariantOverrides],
  } = useDegreeDescriptionEoiStateContext();
  const { stackableItemsWithUpsellType } = useDegreeDescriptionUpsellsData();

  const manualCopyTestObj = slug === 'ms-data-analytics-engineering-northeastern' && getManualCopyTest(slug);
  const manualCopyNortheastern =
    manualCopyTestObj && manualCopyTestObj.epicParam === 'manualCopyNortheastern' ? manualCopyTestObj : undefined;

  if (!slug || !eoiButtons || eoiButtons?.length === 0) {
    return null;
  }

  // DDP Manual Copy Tests
  const manualCopyButtonsOverride = eoiButtons?.map((eoiButton) => {
    if (eoiButton.actionType === ACTION_TYPES.apply && manualCopyNortheastern) {
      return {
        ...eoiButton,
        buttonOptions: {
          ...eoiButton.buttonOptions,
          label: manualCopyNortheastern.applyEnrollBtn,
        },
      };
    } else {
      return eoiButton;
    }
  });

  const eoiButtonsToUse = manualCopyButtonsOverride ?? eoiButtons;

  return (
    <>
      {eoiButtonsToUse.map((eoiButton) => {
        let eoiButtonComponent = (
          <EoiButton
            actionConfig={eoiButton}
            expressionOfInterestText={eoiContainerText}
            key={!enableGrid ? `${eoiButton.actionType}${keySuffix}` : undefined}
            trackingNamePrefix={trackingNamePrefix}
            trackingIndex={trackingIndex}
            setEoiTrackingIndex={setEoiTrackingIndex}
            trackingProductId={id.replace('base~', '')}
          />
        );

        const isEoiButton =
          eoiButton.actionType === ACTION_TYPES.emailMeInfo || eoiButton.actionType === ACTION_TYPES.requestInfo;
        if (
          isEoiButton &&
          shouldShowEoiStateVariant(
            slug,
            stackableItemsWithUpsellType,
            showVariantOverrides,
            eoiSurveyData,
            eoiStateVarState,
            'A'
          )
        ) {
          return null;
        } else if (
          isEoiButton &&
          shouldShowEoiStateVariant(
            slug,
            stackableItemsWithUpsellType,
            showVariantOverrides,
            eoiSurveyData,
            eoiStateVarState,
            'B'
          )
        ) {
          eoiButtonComponent = <TakeACourseBtn slug={slug} />;
        }

        return (
          <>
            {enableGrid ? (
              <Grid item key={enableGrid ? `${eoiButton.actionType}${keySuffix}` : undefined}>
                {eoiButtonComponent}
              </Grid>
            ) : (
              eoiButtonComponent
            )}
          </>
        );
      })}
    </>
  );
};

export default EoiButtonsShared;
